import { useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  MenuItem,
} from "@mui/material";

import Loader from "../../components/Loader";
import useGetPatientLogs from "../../hooks/useGetPatientLogs";
import { CustomDatePicker } from "../../components/UI/CustomDatePicker";

dayjs.extend(utc);

const PatientActivity = () => {
  const { _id_ } = useParams();
  const { setActive, setBreadcrumbTitle } = useOutletContext();

  useEffect(() => {
    setActive("activity");
    setBreadcrumbTitle("Activity");
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 500),
    []
  );

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const { data, isLoading, refetch } = useGetPatientLogs({
    patientId: _id_,
    page,
    pageSize,
    status,
    search,
    startDate,
    endDate,
  });

  const pageCount = Math.ceil((data?.count || 0) / pageSize);

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Subject Activity</Typography>

        <Button variant="contained" size="small" onClick={refetch}>
          Refresh
        </Button>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          label="Search"
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search description or api"
          fullWidth
        />

        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="200">200</MenuItem>
          <MenuItem value="400">400</MenuItem>
          <MenuItem value="401">401</MenuItem>
          <MenuItem value="403">403</MenuItem>
          <MenuItem value="500">500</MenuItem>
        </TextField>

        <CustomDatePicker
          label="Start Date"
          mode="datetime"
          variant="outlined"
          value={startDate}
          onChange={(value) => {
            setPage(1);
            setStartDate(value);
          }}
        />

        <CustomDatePicker
          label="End Date"
          mode="datetime"
          variant="outlined"
          value={endDate}
          minDateTime={startDate ? dayjs(startDate) : undefined}
          onChange={(value) => {
            setPage(1);
            setEndDate(value);
          }}
        />
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Timestamp</TableCell>
              <TableCell sx={headerCellSx}>Description</TableCell>
              <TableCell sx={headerCellSx}>API</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Execution Time</TableCell>
              <TableCell sx={headerCellSx}>Platform</TableCell>
              <TableCell sx={headerCellSx}>App Version</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && data?.results?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No activity found</Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              data?.results?.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={bodyCellSx}>
                    {row.created_at
                      ? dayjs.utc(row.created_at).local().format("YYYY-MM-DD HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{row.description || "-"}</TableCell>
                  <TableCell sx={bodyCellSx}>{row.api || "-"}</TableCell>
                  <TableCell sx={bodyCellSx}>{row.status || "-"}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {row.execution_time ? `${row.execution_time}s` : "-"}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{row.platform || "-"}</TableCell>
                  <TableCell sx={bodyCellSx}>{row.app_version || "-"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Paper>
  );
};

export default PatientActivity;
